import { startOfDay, subWeeks } from 'date-fns';
import { useMemo } from 'react';

import { parseEarningsInsightFromEvents } from '~/react-query-hooks/events/use-event-feed/earnings-from-events';
import { mergeScenarioInsightsWithEvents } from '~/react-query-hooks/events/use-event-feed/scenario-from-events';
import { useEventFeed } from '~/react-query-hooks/events/use-event-feed/useEventFeed';
import { useFilteredInsights } from '~/react-query-hooks/insights/useFilteredInsights';
import { useSubscription } from '~/stores/use-subscription/useSubscription';
import { METHOD_SUB_CLS_GROUPING } from '~/widgets/asset-overview-widget/method_sub_cls_grouping';

import { useAssetsTableStore } from '../../useAssetsTableStore';

const EARNINGS_INSIGHT_KEY = 'earnings';
const SCENARIO_INSIGHT_KEY = 'scenario';
const KG_INSIGHT_KEY = 'kg';

export interface UseInsightsCellDataParams {
  entityTag: string;
}

export const useInsightsCellData = ({
  entityTag,
}: UseInsightsCellDataParams) => {
  const isBasicOrLightUser = useSubscription(state =>
    state.isBasicOrLightUser()
  );
  const canShowCompanyCatalysts = !isBasicOrLightUser;
  const insightsFilterState = useAssetsTableStore(
    state => state.insightsFilterState
  );

  const fromDate = useMemo(() => startOfDay(subWeeks(new Date(), 1)), []);

  const showEarningsInsight = useMemo(() => {
    const kgInsightsFilterState = insightsFilterState[KG_INSIGHT_KEY];
    return kgInsightsFilterState.find(item => item.key === EARNINGS_INSIGHT_KEY)
      ?.checked;
  }, [insightsFilterState]);

  const { data: earningsEventFeedData, isFetching: isEarningsFeedFetching } =
    useEventFeed(
      {
        entities: [entityTag],
        type: 'earnings-insight',
        date_from: fromDate.toISOString(),
      },
      { enabled: showEarningsInsight && !isBasicOrLightUser }
    );
  const { data: scenarioEventFeedData, isFetching: isScenarioFeedFetching } =
    useEventFeed({
      entities: [entityTag],
      type: 'scenario-insight',
      date_from: fromDate.toISOString(),
    });
  const { data, isLoading: isFilteredInsightsLoading } = useFilteredInsights({
    entity: [entityTag],
  });

  const { data: catalystEventFeedData, isFetching: isCatalystsFetching } =
    useEventFeed(
      {
        entities: [entityTag],
        type: 'attribution-insight',
        date_from: fromDate.toISOString(),
        filters: {
          company: {
            values: [
              { category: 'corporate_actions_strategic_announcements' },
              { category: 'management_leadership_changes' },
              { category: 'financial_operational_updates' },
              { category: 'legal_regulatory_events' },
            ],
          },
        },
      },
      {
        enabled: canShowCompanyCatalysts,
      }
    );

  const earningsInsightWithEvent = useMemo(() => {
    const events =
      earningsEventFeedData?.pages?.flatMap(page => page.events) ?? [];
    return parseEarningsInsightFromEvents(events);
  }, [earningsEventFeedData?.pages]);

  const filteredScenarioInsights = useMemo(() => {
    const allAssetInsights = data?.data?.result ?? [];
    const scenarioInsightsFilterState =
      insightsFilterState[SCENARIO_INSIGHT_KEY];

    const filtered =
      Object.values(scenarioInsightsFilterState).every(item => item.checked)
        ? allAssetInsights
        : allAssetInsights.filter(insight => {
            const analysisType =
              METHOD_SUB_CLS_GROUPING[insight.metadata.topic];
            return analysisType
              ? scenarioInsightsFilterState.find(
                  item => item.key === analysisType
                )?.checked
              : false;
          });

    const scenarioEvents =
      scenarioEventFeedData?.pages?.flatMap(page => page.events) ?? [];
    return mergeScenarioInsightsWithEvents(filtered, scenarioEvents);
  }, [
    data?.data?.result,
    insightsFilterState,
    scenarioEventFeedData?.pages,
  ]);

  const filteredCatalystEvents = useMemo(() => {
    const events =
      catalystEventFeedData?.pages?.flatMap(page => page.events) ?? [];
    const kgInsightsFilterState = insightsFilterState[KG_INSIGHT_KEY];

    return events.filter(event => {
      const category = event.metadata.category;
      const analysisType = category
        ? METHOD_SUB_CLS_GROUPING[category]
        : undefined;
      return analysisType
        ? kgInsightsFilterState.find(item => item.key === analysisType)?.checked
        : false;
    });
  }, [catalystEventFeedData?.pages, insightsFilterState]);

  const isLoading =
    isFilteredInsightsLoading ||
    isEarningsFeedFetching ||
    isCatalystsFetching ||
    isScenarioFeedFetching;

  return {
    isLoading,
    filteredScenarioInsights,
    filteredCatalystEvents,
    earningsInsightWithEvent,
  };
};
